/**
 * Finding a leader's way: the shortest line from the callout to its landing that keeps clear
 * of everything else on the plan — every mark, every other callout and note, and the leaders
 * already drawn.
 *
 * The plan's obstacles are few and the shortest way round polygons always bends at their
 * corners, so this is a visibility graph searched with A*: the nodes are points standing a
 * little off the obstacles' convex corners (`reach`), an edge is good when it stays further
 * than `clear` (< reach) from every obstacle. Only the obstacles that actually get in the way
 * are given nodes (found as the search runs into them), which keeps the graph tiny: the usual
 * leader — nothing in the way — costs one visibility test.
 *
 * Pure, in image pixels.
 */
import type { Vec } from '../../shared/doc.ts';
import { dist, pointInPoly, segmentIntersectsRect, segmentsIntersect, simplify, type Rect } from './geom.ts';

export interface Obstacle {
  /** Everything it keeps clear, as a box (a quick way to rule it out). */
  bounds: Rect;
  /** Does the segment come nearer than it may? */
  blocks(a: Vec, b: Vec): boolean;
  /** The points to go round it by. */
  nodes: readonly Vec[];
  /** For a line, which has no corners of its own: its two ends. The way round it is round what it joins. */
  ends?: readonly Vec[];
}

/** A box (a callout, a note) kept clear by `clear`, gone round at `reach` from its corners. */
export function rectObstacle(r: Rect, clear: number, reach: number): Obstacle {
  const keep = grow(r, clear);
  const out = grow(r, Math.max(reach, clear + 0.5));
  return {
    bounds: keep,
    blocks: (a, b) => segmentIntersectsRect(a, b, keep),
    nodes: [
      { x: out.x, y: out.y },
      { x: out.x + out.w, y: out.y },
      { x: out.x + out.w, y: out.y + out.h },
      { x: out.x, y: out.y + out.h },
    ],
  };
}

/** A leader already drawn: not to be crossed. There is no going round a line but round what it joins — its box, its mark. */
export function lineObstacle(pts: readonly Vec[]): Obstacle {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return {
    bounds: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
    blocks: (a, b) => {
      for (let i = 0; i + 1 < pts.length; i++) if (segmentsIntersect(a, b, pts[i]!, pts[i + 1]!)) return true;
      return false;
    },
    nodes: [],
    ends: pts.length ? [pts[0]!, pts[pts.length - 1]!] : [],
  };
}

/**
 * A mark (a closed polygon, flat [x0,y0,…]) kept clear by `clear` and gone round at `reach`.
 * Its border is first reduced to the corners that matter (`tol`, allowed for in both distances).
 */
export function polyObstacle(pts: number[], bounds: Rect, clear: number, reach: number, tol: number): Obstacle {
  const ring = simplifyRing(pts, tol);
  const keepOff = clear + tol;
  const off = Math.max(reach, clear + 0.5) + tol;
  const n = ring.length / 2;
  const at = (i: number): Vec => ({ x: ring[2 * ((i + n) % n)]!, y: ring[2 * ((i + n) % n) + 1]! });
  let area = 0;
  for (let i = 0; i < n; i++) area += at(i).x * at(i + 1).y - at(i + 1).x * at(i).y;
  const ccw = area > 0 ? 1 : -1; // in image coordinates: which hand the inside is on
  const nodes: Vec[] = [];
  for (let i = 0; i < n; i++) {
    const p = at(i - 1),
      v = at(i),
      q = at(i + 1);
    const e1 = unit(v.x - p.x, v.y - p.y),
      e2 = unit(q.x - v.x, q.y - v.y);
    if (!e1 || !e2) continue;
    const turn = (e1.x * e2.y - e1.y * e2.x) * ccw;
    if (turn <= 1e-6) continue; // a hollow: the shortest way never bends there
    // the outward normals of the two edges, and the fan from one to the other round the corner
    const n1 = { x: e1.y * ccw, y: -e1.x * ccw },
      n2 = { x: e2.y * ccw, y: -e2.x * ccw };
    const theta = Math.acos(Math.max(-1, Math.min(1, n1.x * n2.x + n1.y * n2.y)));
    if (theta < Math.PI / 6) {
      const m = unit(n1.x + n2.x, n1.y + n2.y)!;
      const d = off / Math.cos(theta / 2);
      nodes.push({ x: v.x + m.x * d, y: v.y + m.y * d });
      continue;
    }
    const k = Math.ceil(theta / (Math.PI / 3));
    const step = theta / k;
    const d = off / Math.cos(step / 2);
    const a0 = Math.atan2(n1.y, n1.x);
    const dirn = n1.x * n2.y - n1.y * n2.x >= 0 ? 1 : -1;
    for (let j = 0; j <= k; j++) nodes.push({ x: v.x + Math.cos(a0 + dirn * j * step) * d, y: v.y + Math.sin(a0 + dirn * j * step) * d });
  }
  const poly = { pts: ring };
  return {
    bounds: grow(bounds, keepOff),
    blocks: (a, b) => {
      if (crossesRing(a, b, poly.pts)) return true;
      // wholly to one side of the mark's box by more than the clearance: cannot be near it
      if (Math.max(a.x, b.x) < bounds.x - keepOff || Math.min(a.x, b.x) > bounds.x + bounds.w + keepOff || Math.max(a.y, b.y) < bounds.y - keepOff || Math.min(a.y, b.y) > bounds.y + bounds.h + keepOff) return false;
      const l = dist(a, b);
      if (l < 1e-6) return false;
      const nx = (-(b.y - a.y) / l) * keepOff,
        ny = ((b.x - a.x) / l) * keepOff;
      return crossesRing({ x: a.x + nx, y: a.y + ny }, { x: b.x + nx, y: b.y + ny }, poly.pts) || crossesRing({ x: a.x - nx, y: a.y - ny }, { x: b.x - nx, y: b.y - ny }, poly.pts);
    },
    nodes,
  };
}

/** Does a segment cross a closed border, or lie inside it? (One that crosses no edge is inside or outside as a whole.) */
function crossesRing(a: Vec, b: Vec, ring: number[]): boolean {
  const n = ring.length / 2;
  const dx = b.x - a.x,
    dy = b.y - a.y;
  let px = ring[2 * n - 2]!,
    py = ring[2 * n - 1]!;
  let sp = dx * (py - a.y) - dy * (px - a.x); // which hand of the segment's line the vertex is on
  for (let i = 0; i < n; i++) {
    const qx = ring[2 * i]!,
      qy = ring[2 * i + 1]!;
    const sq = dx * (qy - a.y) - dy * (qx - a.x);
    if ((sp > 0 && sq < 0) || (sp < 0 && sq > 0)) {
      // the edge straddles the segment's line: does the segment straddle the edge's?
      const ex = qx - px,
        ey = qy - py;
      const sa = ex * (a.y - py) - ey * (a.x - px),
        sb = ex * (b.y - py) - ey * (b.x - px);
      if ((sa > 0 && sb < 0) || (sa < 0 && sb > 0)) return true;
    }
    px = qx;
    py = qy;
    sp = sq;
  }
  return pointInPoly({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, ring);
}

/** A closed border reduced to its corners: split at its two far ends, each half simplified. */
function simplifyRing(pts: number[], tol: number): number[] {
  const n = pts.length / 2;
  if (n <= 8) return pts;
  let far = 0,
    fd = -1;
  for (let i = 1; i < n; i++) {
    const d = Math.hypot(pts[2 * i]! - pts[0]!, pts[2 * i + 1]! - pts[1]!);
    if (d > fd) {
      fd = d;
      far = i;
    }
  }
  const a = simplify(pts.slice(0, 2 * far + 2), tol);
  const b = simplify([...pts.slice(2 * far), pts[0]!, pts[1]!], tol);
  return [...a.slice(0, -2), ...b.slice(0, -2)];
}

function unit(x: number, y: number): Vec | null {
  const l = Math.hypot(x, y);
  return l < 1e-9 ? null : { x: x / l, y: y / l };
}
function grow(r: Rect, d: number): Rect {
  return { x: r.x - d, y: r.y - d, w: r.w + 2 * d, h: r.h + 2 * d };
}

/** Whether a segment is kept out by any of the obstacles: the first one's index, else −1. */
export function blockedBy(a: Vec, b: Vec, obstacles: readonly Obstacle[]): number {
  const x0 = Math.min(a.x, b.x),
    x1 = Math.max(a.x, b.x),
    y0 = Math.min(a.y, b.y),
    y1 = Math.max(a.y, b.y);
  for (let k = 0; k < obstacles.length; k++) {
    const o = obstacles[k]!;
    const r = o.bounds;
    if (x1 < r.x || x0 > r.x + r.w || y1 < r.y || y0 > r.y + r.h) continue;
    if (o.blocks(a, b)) return k;
  }
  return -1;
}
/** Whether a polyline is clear of every obstacle. */
export function pathClear(pts: readonly Vec[], obstacles: readonly Obstacle[]): boolean {
  for (let i = 0; i + 1 < pts.length; i++) if (blockedBy(pts[i]!, pts[i + 1]!, obstacles) >= 0) return false;
  return true;
}

/**
 * The shortest way from `start` to `goal` clear of the obstacles, within `within` (the plan):
 * the points to pass, both ends included — or null when there is no way at all. The search goes
 * round at most `maxActive` obstacles: a leader that would have to wind round more than that
 * (a crowd of tests) is not a leader worth drawing, and looking for one would cost seconds.
 */
export function findPath(start: Vec, goal: Vec, obstacles: readonly Obstacle[], within: Rect, maxActive = 16): Vec[] | null {
  if (blockedBy(start, goal, obstacles) < 0) return [start, goal];
  // every node there could be, by obstacle; the graph holds start, goal and the active obstacles' nodes
  const pts: Vec[] = [start, goal];
  const owner: number[] = [-1, -1];
  obstacles.forEach((o, k) => {
    for (const p of o.nodes) {
      if (p.x < within.x || p.y < within.y || p.x > within.x + within.w || p.y > within.y + within.h) continue;
      pts.push(p);
      owner.push(k);
    }
  });
  const N = pts.length;
  const seen = new Map<number, number>(); // pair → blocking obstacle (−1 = clear)
  const active = new Set<number>();
  const look = (i: number, j: number): number => {
    const key = i < j ? i * N + j : j * N + i;
    let v = seen.get(key);
    if (v === undefined) {
      v = blockedBy(pts[i]!, pts[j]!, obstacles);
      seen.set(key, v);
    }
    return v;
  };
  let best: Vec[] | null = null;
  for (let round = 0; round < 8; round++) {
    const found = new Set<number>();
    const g = new Float64Array(N).fill(Infinity);
    const from = new Int32Array(N).fill(-1);
    const done = new Uint8Array(N);
    const open: number[] = [0];
    g[0] = 0;
    let reached = false;
    while (open.length) {
      let bi = 0;
      for (let k = 1; k < open.length; k++) if (g[open[k]!]! + dist(pts[open[k]!]!, goal) < g[open[bi]!]! + dist(pts[open[bi]!]!, goal)) bi = k;
      const cur = open.splice(bi, 1)[0]!;
      if (done[cur]) continue;
      done[cur] = 1;
      if (cur === 1) {
        reached = true;
        break;
      }
      for (let j = 1; j < N; j++) {
        if (done[j] || (j > 1 && !active.has(owner[j]!))) continue;
        const d = g[cur]! + dist(pts[cur]!, pts[j]!);
        if (d >= g[j]!) continue;
        const by = look(cur, j);
        if (by >= 0) {
          if (!active.has(by)) found.add(by);
          continue;
        }
        g[j] = d;
        from[j] = cur;
        open.push(j);
      }
    }
    if (reached) {
      const path: Vec[] = [];
      for (let k = 1; k >= 0; k = from[k]!) {
        path.unshift(pts[k]!);
        if (k === 0) break;
      }
      best = path;
    }
    // what got in the way and had no corners in the graph yet: go round those too, and look again
    let grew = false;
    let crowded = false;
    const activate = (k: number) => {
      if (!obstacles[k]!.nodes.length || active.has(k)) return;
      if (active.size >= maxActive) {
        crowded = true;
        return;
      }
      active.add(k);
      grew = true;
    };
    for (const k of found) {
      activate(k);
      // a line in the way: round the box it leaves or the mark it reaches
      for (const e of obstacles[k]!.ends ?? [])
        obstacles.forEach((o, j) => {
          const r = o.bounds;
          if (e.x >= r.x - 1 && e.x <= r.x + r.w + 1 && e.y >= r.y - 1 && e.y <= r.y + r.h + 1) activate(j);
        });
    }
    if (!grew || crowded) break;
  }
  return best;
}
